import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Divider, IconButton, InputAdornment, LinearProgress, Paper, Radio, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import SearchIcon from "@mui/icons-material/Search";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import InputIcon from "@mui/icons-material/Input";
import { EnlazarBancoDialog, EnlazarTraspasoDialog, RelacionesDialog } from "./ConciliacionDialogs";
import {
  enlazarCreditosConciliacion, enlazarDebitosConciliacion, fmtFecha, fmtMoney, getDisponibles, listarConciliacion, marcarConjuntoCreditos,
  marcarCorrectoConciliacion, vincularCreditosReferencia,
} from "../services/egresosService";

const cellSx = { py: 0.2, px: 0.5, fontSize: 11 };
const OPCIONES_FILAS = [25, 50, 100, 200];
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const FONDO = { success: "#d4edda", warning: "#fff3cd", danger: "#f8d7da" };
// Tarjeta de resumen: etiqueta pequeña y cifra grande, con un filete de color a la izquierda
const Resumen = ({ etiqueta, valor, color, nota }) => (
  <Box sx={{ flex: "1 1 150px", minWidth: 150, px: 1.5, py: 0.9, border: 1, borderColor: "divider", borderLeft: 3, borderLeftColor: color, bgcolor: "background.paper" }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>{etiqueta}</Typography>
    <Typography sx={{ fontSize: 17, fontWeight: 800, lineHeight: 1.25, fontVariantNumeric: "tabular-nums" }}>{valor}</Typography>
    {nota && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{nota}</Typography>}
  </Box>
);

// Campo por el que se ordena cada encabezado (los que no están aquí no se ordenan)
const CAMPO_ORDEN = { "ID": "id", "Fecha": "fecha", "Cód. transacción": "cod_transaccion", "Banco": "banco", "Banco SORT": "banco_sort", "Diferencia": "diferencia", "Estado": "estado" };

const msg = (e, def) => e?.response?.data?.error || def;

// Conciliación del extracto del mes (espejo de busqueda1.php "Correctos" y busqueda3.php "No existen"):
// cada movimiento del banco se compara con lo que registra el sistema y, si falta, se enlaza.
export default function ConciliacionPanel({ mes, year, vista, banco = "" }) {
  const pendientes = vista === "pendientes";
  const [tipo, setTipo] = useState("debitos");
  const [data, setData] = useState({ items: [], total_items: 0, total_extracto: 0, total_sort: 0 });
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(25);
  const [orden, setOrden] = useState({ campo: "", dir: "asc" }); // vacío = el orden de siempre
  const [filtro, setFiltro] = useState(""); // lo que se escribe
  const [busqueda, setBusqueda] = useState(""); // lo que se envía al servidor (con espera)
  const [disp, setDisp] = useState({ egresos: [], recibos: [], pagos: [] });
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [sel, setSel] = useState(null); // movimiento seleccionado (radio)
  const [marcEgresos, setMarcEgresos] = useState({});
  const [marcPagos, setMarcPagos] = useState({});
  const [marcRecibos, setMarcRecibos] = useState({});
  const [relId, setRelId] = useState(null);
  const [bancoId, setBancoId] = useState(null);
  const [traspasoMov, setTraspasoMov] = useState(null);

  // La lista se pagina en el servidor. Al cambiar solo de página se vuelve a pedir la lista, pero se conservan los cheques/pagos marcados a la
  // derecha; al cambiar de mes, tipo o tras una acción se recarga todo.
  const paginaRef = useRef(0);
  const limiteRef = useRef(25);
  const busquedaRef = useRef("");
  const ordenRef = useRef({ campo: "", dir: "asc" });
  paginaRef.current = pagina;
  limiteRef.current = limite;
  busquedaRef.current = busqueda;
  ordenRef.current = orden;

  const traer = useCallback(async (conDisponibles) => {
    setCargando(true);
    setError("");
    try {
      const [l, d] = await Promise.all([
        listarConciliacion({ mes, year, vista, tipo, page: paginaRef.current + 1, limit: limiteRef.current, q: busquedaRef.current || undefined, banco: banco || undefined, orden: ordenRef.current.campo || undefined, dir: ordenRef.current.campo ? ordenRef.current.dir : undefined }),
        pendientes && conDisponibles ? getDisponibles({ mes, year, tipo }) : Promise.resolve(null),
      ]);
      setData(l.data);
      setSel(null);
      if (d) {
        setDisp({ egresos: [], recibos: [], pagos: [], ...d.data });
        setMarcEgresos({}); setMarcPagos({}); setMarcRecibos({});
      }
    } catch (e) {
      setError(msg(e, "No se pudo cargar la conciliación."));
    } finally {
      setCargando(false);
    }
  }, [mes, year, vista, tipo, pendientes, banco]);

  const cargar = useCallback(() => traer(true), [traer]);

  useEffect(() => { cargar(); }, [cargar]);

  // El buscador espera un momento antes de consultar y siempre vuelve a la primera página.
  useEffect(() => {
    const t = setTimeout(() => { setBusqueda(filtro.trim()); setPagina(0); }, 350);
    return () => clearTimeout(t);
  }, [filtro]);

  const primeraPagina = useRef(true);
  useEffect(() => {
    if (primeraPagina.current) { primeraPagina.current = false; return; }
    traer(false);
  }, [pagina, limite, busqueda, orden]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si tras una acción (p. ej. enviar a correctos) la página actual queda vacía, se vuelve a la última que tenga datos.
  useEffect(() => {
    if (!cargando && !data.items.length && data.total_items > 0 && pagina > 0) setPagina(Math.max(Math.ceil(data.total_items / limite) - 1, 0));
  }, [cargando, data.items.length, data.total_items, pagina, limite]);

  const ids = (m) => Object.keys(m).filter((k) => m[k]).map(Number);
  const hayMarcados = tipo === "debitos" ? ids(marcEgresos).length > 0 : ids(marcPagos).length + ids(marcRecibos).length > 0;

  const ejecutar = async (fn, mensaje) => {
    setError("");
    setOk("");
    try {
      const { data: r } = await fn();
      setOk(typeof mensaje === "function" ? mensaje(r) : mensaje);
      await cargar();
    } catch (e) {
      setError(msg(e, "No se pudo completar la acción."));
    }
  };

  const enlazar = () => {
    if (!sel) { setError("Selecciona un movimiento del extracto."); return; }
    if (tipo === "debitos") ejecutar(() => enlazarDebitosConciliacion({ extractoId: sel, egresoIds: ids(marcEgresos) }), (r) => `${r.enlazados} cheque(s) enlazado(s).`);
    else ejecutar(() => enlazarCreditosConciliacion({ extractoId: sel, pagoIds: ids(marcPagos), reciboIds: ids(marcRecibos) }), (r) => `${r.enlazados} registro(s) enlazado(s).`);
  };

  const dif = data.total_extracto - data.total_sort;
  // "Enviar todos a correctos" depende de los totales del mes completo, no de lo filtrado.
  const conjuntoOk = tipo === "creditos" && pendientes && data.total_items > 0 && Math.abs(dif) < 1;
  const etiqueta = tipo === "debitos" ? "Débitos" : "Créditos";
  const campoDe = (h) => CAMPO_ORDEN[h] || (h === etiqueta ? "extracto" : h === `${etiqueta} SORT` ? "sort" : "");

  return (
    <Box>
      <Tabs value={tipo} onChange={(_, v) => { setTipo(v); setPagina(0); setFiltro(""); setBusqueda(""); }} sx={{ minHeight: 38, mb: 1.25, "& .MuiTab-root": { minHeight: 38, py: 0.5, px: 2.5, fontSize: 14, textTransform: "none", fontWeight: 700 } }}>
        <Tab value="debitos" label="Débitos" />
        <Tab value="creditos" label="Créditos" />
      </Tabs>
      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError("")}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 1, py: 0 }} onClose={() => setOk("")}>{ok}</Alert>}

      {!pendientes && (() => {
        const ext = busqueda ? data.filtro_extracto || 0 : data.total_extracto;
        const sis = busqueda ? data.filtro_sort || 0 : data.total_sort;
        const d = ext - sis;
        const cuadra = Math.abs(d) < 0.01;
        return (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.25 }}>
            <Resumen etiqueta="Movimientos" valor={data.total_items} color="#546e7a" nota={busqueda ? "que coinciden con la búsqueda" : `${etiqueta.toLowerCase()} de ${MESES[mes - 1]} ${year}`} />
            <Resumen etiqueta="Según el banco" valor={fmtMoney(ext)} color="#0b6e6e" nota="total del extracto" />
            <Resumen etiqueta="Según el sistema" valor={fmtMoney(sis)} color="#1565c0" nota="total registrado en SORT" />
            <Resumen etiqueta="Diferencia" valor={fmtMoney(d)} color={cuadra ? "#2e7d32" : "#c62828"} nota={cuadra ? "✓ Cuadra: banco y sistema coinciden" : "No cuadra: revisa los movimientos"} />
          </Box>
        );
      })()}

      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: { xs: "wrap", lg: "nowrap" } }}>
        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <Box sx={{ p: 1.75, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.2 }}>{etiqueta} {pendientes ? "sin enlazar" : "correctos"}</Typography>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {busqueda && data.total_general !== undefined
                  ? `${data.total_items} de ${data.total_general} movimientos coinciden con la búsqueda`
                  : `${data.total_items} movimiento${data.total_items === 1 ? "" : "s"} · ${pendientes ? "falta enlazarlos con cheques o pagos del sistema" : "ya coinciden con lo registrado en el sistema"}`}
              </Typography>
            </Box>
            {tipo === "creditos" && pendientes && (
              <Tooltip title="Vincula automáticamente los pagos cuya referencia coincide con el extracto">
                <Button
                  size="small" variant="outlined" startIcon={<SyncIcon />} sx={{ textTransform: "none", fontWeight: 700 }}
                  onClick={() => ejecutar(() => vincularCreditosReferencia({ mes, year }), (r) => `Revisados ${r.revisados}, pagos vinculados ${r.vinculados}.`)}
                >
                  Actualizar crédito
                </Button>
              </Tooltip>
            )}
          </Box>
          <Divider />
          <Box sx={{ px: 1.75, py: 1.25, bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.05 : 0.03) }}>
            <TextField
              size="small" placeholder="Buscar código, banco, monto…" value={filtro} onChange={(e) => setFiltro(e.target.value)}
              sx={{ width: { xs: "100%", sm: 340 }, bgcolor: "background.paper", "& .MuiInputBase-input": { py: "6px", fontSize: 13 } }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            />
          </Box>
          {cargando && <LinearProgress sx={{ height: 2 }} />}
          <TableContainer sx={pendientes ? undefined : { maxHeight: "62vh" }}>
            <Table size="small" stickyHeader={!pendientes} sx={{ "& .MuiTableCell-root": cellSx, ...(pendientes ? {} : { "& .MuiTableCell-stickyHeader": { bgcolor: "background.paper", borderBottom: 2, borderColor: "divider" } }) }}>
              <TableHead>
                <TableRow>
                  {(pendientes
                    ? ["", "ID", "Cód. transacción", "Banco", "Banco SORT", "Fecha", etiqueta, `${etiqueta} SORT`, "Diferencia", ...(tipo === "debitos" ? ["Adicional", "Traspaso", "Extracto"] : ["Traspaso", "Adicional"]), "Acción"]
                    : ["Fecha", "Cód. transacción", "Banco", "Banco SORT", etiqueta, `${etiqueta} SORT`, "Diferencia", "Detalle", "Estado"]
                  ).map((h, i) => (
                    <TableCell
                      key={`${h}-${i}`} padding={pendientes && i === 0 ? "checkbox" : "normal"}
                      align={!pendientes && [4, 5, 6].includes(i) ? "right" : "left"}
                      sortDirection={campoDe(h) && orden.campo === campoDe(h) ? orden.dir : false}
                      sx={{ fontWeight: 800, whiteSpace: "nowrap", ...(pendientes ? {} : { fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3, color: "text.secondary" }) }}
                    >
                      {(() => {
                        const campo = campoDe(h);
                        if (!campo) return h;
                        const activo = orden.campo === campo;
                        return (
                          <TableSortLabel
                            active={activo} direction={activo ? orden.dir : "asc"} hideSortIcon
                            onClick={() => { setOrden(activo ? (orden.dir === "asc" ? { campo, dir: "desc" } : { campo: "", dir: "asc" }) : { campo, dir: "asc" }); setPagina(0); }}
                            sx={{ "&:hover": { color: "primary.main" }, "&.Mui-active": { color: "primary.main" } }}
                          >{h}</TableSortLabel>
                        );
                      })()}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!data.items.length ? (
                  <TableRow>
                    <TableCell colSpan={13} sx={{ py: 0 }}>
                      {cargando ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>Cargando…</Typography>
                      ) : busqueda ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>Ningún movimiento coincide con la búsqueda.</Typography>
                      ) : (
                        <Box sx={{ py: 4, display: "grid", justifyItems: "center", gap: 0.75, textAlign: "center" }}>
                          <Box sx={{ width: 52, height: 52, borderRadius: "50%", display: "grid", placeItems: "center", color: pendientes ? "success.main" : "text.secondary", bgcolor: (t) => alpha(pendientes ? t.palette.success.main : t.palette.text.primary, pendientes ? 0.12 : 0.07) }}>
                            {pendientes ? <CheckCircleOutlineIcon /> : <InboxOutlinedIcon />}
                          </Box>
                          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                            {pendientes ? `No hay ${etiqueta.toLowerCase()} pendientes en ${MESES[mes - 1]} ${year}` : `No hay ${etiqueta.toLowerCase()} correctos en ${MESES[mes - 1]} ${year}`}
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: "text.secondary", maxWidth: 420 }}>
                            {pendientes ? "Todo lo que trae el extracto de este mes ya está enlazado." : "Cuando un movimiento coincida con lo registrado en el sistema aparecerá aquí."}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ) : !pendientes ? data.items.map((r) => {
                  const cuadra = Math.abs(Number(r.diferencia) || 0) < 0.01;
                  return (
                    <TableRow key={r.id} hover sx={{ "&:nth-of-type(even)": { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) } }}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {fmtFecha(r.fecha)}
                        <Typography component="span" sx={{ display: "block", fontSize: 10, color: "text.disabled", lineHeight: 1 }}>#{r.id}</Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Box component="a" href="#rel" onClick={(e) => { e.preventDefault(); setRelId(r.id); }} sx={{ color: "primary.main", cursor: "pointer", fontFamily: "monospace", fontWeight: 700, textDecoration: "underline dotted", "&:hover": { textDecoration: "underline" } }}>
                          {r.cod_transaccion || "Sin código"}
                        </Box>
                        {r.referencia && <Typography component="span" sx={{ display: "block", fontSize: 10.5, color: "text.secondary", lineHeight: 1.1 }}>{r.referencia}</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{r.banco}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{r.banco_sort}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmtMoney(r.extracto)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{fmtMoney(r.sort)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", fontWeight: cuadra ? 400 : 800, color: cuadra ? "text.disabled" : "error.main" }}>{fmtMoney(r.diferencia)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                          {r.con_traspaso ? <Chip size="small" variant="outlined" label="Traspaso" sx={{ height: 18, fontSize: 10.5 }} /> : null}
                          {tipo === "debitos" && r.con_extracto ? <Chip size="small" variant="outlined" label="Extracto" sx={{ height: 18, fontSize: 10.5 }} /> : null}
                          {r.adicional && <Typography component="span" title={r.adicional} sx={{ fontSize: 11, color: "text.secondary", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.adicional}</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {cuadra
                          ? <Chip size="small" color="success" variant="outlined" icon={<CheckCircleOutlineIcon />} label="Correcto" sx={{ height: 20, fontSize: 11, fontWeight: 700, "& .MuiChip-icon": { fontSize: 15 } }} />
                          : <Chip size="small" color="warning" variant="outlined" icon={<WarningAmberIcon />} label="Revisar" sx={{ height: 20, fontSize: 11, fontWeight: 700, "& .MuiChip-icon": { fontSize: 15 } }} />}
                      </TableCell>
                    </TableRow>
                  );
                }) : data.items.map((r) => {
                  const f = { bgcolor: FONDO[r.clase], "& .MuiTableCell-root": { color: "#1a1a1a" } };
                  return (
                    <TableRow key={r.id} sx={f}>
                      <TableCell padding="checkbox"><Radio size="small" sx={{ p: 0.3 }} checked={sel === r.id} onChange={() => setSel(r.id)} /></TableCell>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>
                        <Box component="a" href="#rel" onClick={(e) => { e.preventDefault(); setRelId(r.id); }} sx={{ color: "#0b57d0", cursor: "pointer" }}>
                          {r.cod_transaccion || <b>Sin código de transacción</b>}
                        </Box> {r.referencia ? `(${r.referencia})` : ""}
                      </TableCell>
                      <TableCell>{r.banco}</TableCell><TableCell>{r.banco_sort}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtFecha(r.fecha)}</TableCell>
                      <TableCell align="right">{fmtMoney(r.extracto)}</TableCell><TableCell align="right">{fmtMoney(r.sort)}</TableCell>
                      <TableCell align="right">{fmtMoney(r.diferencia)}</TableCell>
                      {tipo === "debitos" ? (
                        <>
                          <TableCell sx={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.adicional}>{r.adicional}</TableCell>
                          <TableCell>{r.con_traspaso ? "si" : ""}</TableCell><TableCell>{r.con_extracto ? "si" : ""}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{r.con_traspaso ? "si" : ""}</TableCell>
                          <TableCell sx={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.adicional}>{r.adicional}</TableCell>
                        </>
                      )}
                      {pendientes && (
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {r.puede_correcto ? (
                            <Tooltip title="Enviar a datos correctos">
                              <IconButton size="small" sx={{ p: 0.2, color: "#1a7f37" }} onClick={() => ejecutar(() => marcarCorrectoConciliacion({ id: r.id }), "Movimiento enviado a datos correctos.")}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
                            </Tooltip>
                          ) : (
                            <>
                              <Tooltip title="Enlazar con datos del banco"><IconButton size="small" sx={{ p: 0.2 }} onClick={() => setBancoId(r.id)}><SwapVertIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                              {tipo === "debitos" && <Tooltip title="Enlazar con traspaso interno"><IconButton size="small" sx={{ p: 0.2 }} onClick={() => setTraspasoMov(r)}><InputIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
                            </>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {data.items.length > 0 && (
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell colSpan={pendientes ? 6 : 4} sx={{ fontWeight: 800 }} align="right">{busqueda ? `Totales del filtro (${data.total_items} movimientos)` : `Totales de los ${data.total_items} movimientos`}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(busqueda ? data.filtro_extracto : data.total_extracto)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(busqueda ? data.filtro_sort : data.total_sort)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(busqueda ? (data.filtro_extracto || 0) - (data.filtro_sort || 0) : dif)}</TableCell>
                    <TableCell colSpan={pendientes ? 4 : 2}>
                      {conjuntoOk && !busqueda && (
                        <Tooltip title="Enviar todos los créditos a datos correctos">
                          <IconButton size="small" sx={{ p: 0.2, color: "#1a7f37" }}
                            onClick={() => window.confirm("¿Enviar todos los créditos pendientes a datos correctos?") && ejecutar(() => marcarConjuntoCreditos({ mes, year }), (r) => `${r.actualizados} crédito(s) enviados a correctos.`)}>
                            <CheckIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={data.total_items} page={pagina} rowsPerPage={limite} rowsPerPageOptions={OPCIONES_FILAS}
            onPageChange={(_, p) => setPagina(p)} onRowsPerPageChange={(e) => { setLimite(Number(e.target.value)); setPagina(0); }}
            labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ borderTop: 1, borderColor: "divider", "& .MuiTablePagination-toolbar": { minHeight: 40 } }}
          />
        </Paper>

        {pendientes && (
          <Paper variant="outlined" sx={{ width: { xs: "100%", lg: 380 }, flexShrink: 0, overflow: "hidden" }}>
            <Box sx={{ px: 1.75, py: 1.25, bgcolor: "action.hover" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{tipo === "debitos" ? "Cheques del sistema sin enlazar" : "Pagos y recibos del sistema sin enlazar"}</Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Marca los que corresponden al movimiento elegido</Typography>
            </Box>
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableBody>
                  {tipo === "debitos" && (disp.egresos.length ? disp.egresos.map((e) => (
                    <TableRow key={e.id} hover>
                      <TableCell padding="checkbox"><Checkbox size="small" sx={{ p: 0.3 }} checked={!!marcEgresos[e.id]} onChange={() => setMarcEgresos((p) => ({ ...p, [e.id]: !p[e.id] }))} /></TableCell>
                      <TableCell>{e.nocheque}</TableCell><TableCell>{e.id}</TableCell><TableCell sx={{ whiteSpace: "nowrap" }}>{fmtFecha(e.fecha)}</TableCell>
                      <TableCell>{e.banco}</TableCell><TableCell align="right">{fmtMoney(e.moneda === "Dolares" ? e.dolares : e.bolivianos)}</TableCell><TableCell>{e.moneda}</TableCell>
                    </TableRow>
                  )) : <TableRow><TableCell sx={{ py: 2, color: "text.secondary" }}>No hay cheques sin enlazar.</TableCell></TableRow>)}
                  {tipo === "creditos" && (
                    <>
                      <TableRow><TableCell colSpan={5} sx={{ fontWeight: 800 }}>Recibo crédito</TableCell></TableRow>
                      {disp.recibos.length ? disp.recibos.map((r) => (
                        <TableRow key={`r${r.id}`} hover>
                          <TableCell padding="checkbox"><Checkbox size="small" sx={{ p: 0.3 }} checked={!!marcRecibos[r.id]} onChange={() => setMarcRecibos((p) => ({ ...p, [r.id]: !p[r.id] }))} /></TableCell>
                          <TableCell>{r.comprobante}</TableCell><TableCell sx={{ whiteSpace: "nowrap" }}>{fmtFecha(r.fecha)}</TableCell><TableCell>{r.banco_destino}</TableCell><TableCell align="right">{fmtMoney(r.monto)}</TableCell>
                        </TableRow>
                      )) : <TableRow><TableCell colSpan={5}>Sin recibos.</TableCell></TableRow>}
                      <TableRow><TableCell colSpan={5} sx={{ fontWeight: 800 }}>Créditos SORT</TableCell></TableRow>
                      {disp.pagos.length ? disp.pagos.map((p) => (
                        <TableRow key={`p${p.cod}`} hover>
                          <TableCell padding="checkbox"><Checkbox size="small" sx={{ p: 0.3 }} checked={!!marcPagos[p.cod]} onChange={() => setMarcPagos((q) => ({ ...q, [p.cod]: !q[p.cod] }))} /></TableCell>
                          <TableCell>{p.cod_banco}</TableCell><TableCell sx={{ whiteSpace: "nowrap" }}>{fmtFecha(p.fecha_deposito)}</TableCell><TableCell>{p.banco}</TableCell><TableCell align="right">{fmtMoney(p.monto)}</TableCell>
                        </TableRow>
                      )) : <TableRow><TableCell colSpan={5}>Sin pagos.</TableCell></TableRow>}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 1, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, borderTop: 1, borderColor: "divider" }}>
              <Typography sx={{ fontSize: 11 }} color="text.secondary">{sel ? `Movimiento ${sel}` : "Elige un movimiento (círculo)"}</Typography>
              <Button size="small" variant="contained" disabled={!sel || !hayMarcados} onClick={enlazar}>Enlazar</Button>
            </Box>
          </Paper>
        )}
      </Box>

      <RelacionesDialog id={relId} onClose={() => setRelId(null)} onCambio={cargar} />
      <EnlazarBancoDialog id={bancoId} mes={mes} year={year} onClose={() => setBancoId(null)} onGuardado={(m) => { setOk(m); cargar(); }} />
      <EnlazarTraspasoDialog id={traspasoMov?.id} debito={traspasoMov?.extracto ?? 0} mes={mes} year={year} onClose={() => setTraspasoMov(null)} onGuardado={(m) => { setOk(m); cargar(); }} />
    </Box>
  );
}
